import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type Lead,
  LeadEventsApi,
  type LeadEventWithStatusDto,
  LeadEventLifecycleStatus,
  LeadEventRosterMode,
  LeadStatus,
  Permission,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Pencil, Plus, ScanLine } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { BusinessCardScanFlow } from '@/core/components/leads/business-card-scan-flow';
import { LeadCardList } from '@/core/components/leads/lead-card-list';
import {
  LEAD_DETAIL_SECTION_TITLE_CLASS,
  LeadDetailSectionCard,
} from '@/core/components/leads/lead-detail/lead-detail-section-card';
import { LeadEventAnalytics } from '@/core/components/leads/lead-event-analytics';
import { LeadEventDetailSummary } from '@/core/components/leads/lead-event-detail-summary';
import { LeadEventDetailsForm } from '@/core/components/leads/lead-event-details-form';
import { LeadEventLeadsTable } from '@/core/components/leads/lead-event-leads-table';
import { PageHeader } from '@/core/components/page-header';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useProfile } from '@/core/hooks/use-profile';
import { useWorkspaceMembersRoster } from '@/core/hooks/use-workspace-members-roster';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import {
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import {
  leadEventsQueryKeys,
  leadsQueryKeys,
} from '@/core/query-keys/leads-query-keys';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import {
  getApiBusinessErrorCode,
  getApiErrorStatus,
} from '@/core/utils/api-error.utils';

function LeadEventDetailBreadcrumb({
  workLabel,
  leadsLabel,
  eventsLabel,
}: {
  workLabel: string;
  leadsLabel: string;
  eventsLabel: string;
}) {
  return (
    <>
      {workLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {leadsLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {eventsLabel}
    </>
  );
}

function daysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export function LeadEventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const dict = useDictionary();
  const eventsDict = dict.leads.events;
  const locale = useLocale();
  const isMobile = useIsMobile();
  const { navigateShell } = useNavigateShell();
  const backRouter = useIonBackRouter();
  const goBackToEvents = () =>
    goBackOrFallback(backRouter, {
      href: shellPaths.leadsEvents,
      navigate: () =>
        navigateShell(shellPaths.leadsEvents, { intent: 'switchTab' }),
    });
  const { profile } = useProfile();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { can } = usePermissions();
  const canManage = can(Permission.EVENTS_MANAGE);
  const queryClient = useQueryClient();
  const [businessCardScanOpen, setBusinessCardScanOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createLeadHostId, setCreateLeadHostId] = useState(0);
  // Once deleted, stop fetching this event: a refetch would 404 and flash
  // the error state while the page animates out (DEV-820).
  const [deleted, setDeleted] = useState(false);
  const queriesEnabled = !!workspaceId && !!eventId && !deleted;

  const breadcrumb = (
    <LeadEventDetailBreadcrumb
      workLabel={dict.nav.navDomainWork}
      leadsLabel={dict.nav.leads}
      eventsLabel={eventsDict.title}
    />
  );

  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey:
      workspaceId && eventId
        ? leadEventsQueryKeys.detail(workspaceId, eventId)
        : leadEventsQueryKeys.root,
    queryFn: async () => {
      const res = await LeadEventsApi.findOne({
        path: { workspaceId: workspaceId!, eventId: eventId! },
      });
      return res.data as LeadEventWithStatusDto;
    },
    enabled: queriesEnabled,
    retry: false,
  });

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey:
      workspaceId && eventId
        ? leadEventsQueryKeys.leads(workspaceId, eventId)
        : leadEventsQueryKeys.root,
    queryFn: async () => {
      const res = await LeadEventsApi.findLeads({
        path: { workspaceId: workspaceId!, eventId: eventId! },
      });
      return (res.data ?? []) as Lead[];
    },
    enabled: queriesEnabled,
    staleTime: 30_000,
  });

  const { data: analytics } = useQuery({
    queryKey:
      workspaceId && eventId
        ? leadEventsQueryKeys.analytics(workspaceId, eventId)
        : leadEventsQueryKeys.root,
    queryFn: async () => {
      const res = await LeadEventsApi.getEventAnalytics({
        path: { workspaceId: workspaceId!, eventId: eventId! },
      });
      return res.data;
    },
    enabled: queriesEnabled,
    staleTime: 30_000,
  });

  const { data: members = [] } = useWorkspaceMembersRoster(
    workspaceId,
    'event-analytics',
    { staleTime: 60_000 },
  );

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) {
      const uid = m.userId?.trim();
      if (!uid) continue;
      const u = m.user;
      const name = u
        ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || uid
        : uid;
      map.set(uid, name);
    }
    return map;
  }, [members]);

  const dateFmt = new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const coverUrl = event?.coverImage?.path
    ? getAbsoluteFileUrl(event.coverImage.path)
    : undefined;

  const handleDelete = async () => {
    if (!workspaceId || !eventId) return;
    try {
      await LeadEventsApi.remove({
        path: { workspaceId, eventId },
      });
      setDeleted(true);
      // Keep the cached event so title and dialog keep its name; removing it
      // refetched the deleted event (404) and flashed the error state.
      void queryClient.invalidateQueries({
        queryKey: leadEventsQueryKeys.all(workspaceId),
      });
      toast.success(eventsDict.detail.deleteSuccess);
      goBackToEvents();
    } catch (err: unknown) {
      const code = getApiBusinessErrorCode(err);
      const mapped =
        code && code in eventsDict.form.errors
          ? eventsDict.form.errors[code as keyof typeof eventsDict.form.errors]
          : undefined;
      toast.error(mapped ?? eventsDict.form.deleteError);
      throw err;
    }
  };

  const handleSaved = (updated: LeadEventWithStatusDto) => {
    if (!workspaceId || !eventId) return;
    queryClient.setQueryData(
      leadEventsQueryKeys.detail(workspaceId, eventId),
      updated,
    );
    void queryClient.invalidateQueries({
      queryKey: leadEventsQueryKeys.all(workspaceId),
    });
    setEditOpen(false);
  };

  const invalidateEventLeads = () => {
    if (!workspaceId || !eventId) return;
    void queryClient.invalidateQueries({
      queryKey: leadEventsQueryKeys.leads(workspaceId, eventId),
    });
    void queryClient.invalidateQueries({
      queryKey: leadEventsQueryKeys.analytics(workspaceId, eventId),
    });
    void queryClient.invalidateQueries({
      queryKey: leadsQueryKeys.all(workspaceId),
    });
  };

  const statusLabel =
    event?.status === LeadEventLifecycleStatus.ACTIVE
      ? eventsDict.status.active
      : event?.status === LeadEventLifecycleStatus.UPCOMING
        ? eventsDict.status.upcoming
        : eventsDict.status.past;

  const statusMeta = (() => {
    if (!event) return null;
    const now = new Date();
    if (event.status === LeadEventLifecycleStatus.ACTIVE) {
      const left = daysBetween(now, new Date(event.endsAt));
      return eventsDict.detail.daysLeft.replace('{count}', String(left));
    }
    if (event.status === LeadEventLifecycleStatus.UPCOMING) {
      const left = daysBetween(now, new Date(event.startsAt));
      return eventsDict.detail.startsIn.replace('{count}', String(left));
    }
    return null;
  })();

  const teamLabel =
    event?.rosterMode === LeadEventRosterMode.EVERYONE
      ? eventsDict.form.rosterEveryone
      : eventsDict.table.peopleCount.replace(
          '{count}',
          String(
            Math.max(
              (event?.rosterUserIds.length ?? 0) +
                (event?.rosterTeamIds.length ?? 0),
              1,
            ),
          ),
        );

  const qualifiedPlus = useMemo(() => {
    const byStatus = analytics?.byStatus ?? {};
    return (
      (byStatus[LeadStatus.QUALIFIED] ?? 0) + (byStatus[LeadStatus.WON] ?? 0)
    );
  }, [analytics]);

  const perDay = useMemo(() => {
    if (!event || !analytics || analytics.totalLeads === 0) return '0';
    const start = new Date(event.startsAt).getTime();
    const end = new Date(event.endsAt).getTime();
    const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    const rate = analytics.totalLeads / days;
    return locale === 'de'
      ? rate.toFixed(1).replace('.', ',')
      : rate.toFixed(1);
  }, [analytics, event, locale]);

  const isOnEventTeam = useMemo(() => {
    if (!event || !profile?.id) return true;
    if (event.rosterMode === LeadEventRosterMode.EVERYONE) return true;
    return event.rosterUserIds.includes(profile.id);
  }, [event, profile?.id]);

  const handleAddMeToTeam = async () => {
    if (!workspaceId || !eventId || !event || !profile?.id || !canManage) {
      setEditOpen(true);
      return;
    }
    try {
      const nextUsers = Array.from(
        new Set([...event.rosterUserIds, profile.id]),
      );
      const res = await LeadEventsApi.update({
        path: { workspaceId, eventId },
        body: {
          rosterMode: LeadEventRosterMode.SELECTED,
          rosterUserIds: nextUsers,
          rosterTeamIds: event.rosterTeamIds,
        },
      });
      if (res.data) handleSaved(res.data as LeadEventWithStatusDto);
    } catch {
      toast.error(eventsDict.form.error);
      setEditOpen(true);
    }
  };

  const unusualMonths = useMemo(() => {
    if (!event) return 0;
    const start = new Date(event.startsAt).getTime();
    const end = new Date(event.endsAt).getTime();
    const months = (end - start) / (1000 * 60 * 60 * 24 * 30.44);
    return months > 14 ? Math.round(months) : 0;
  }, [event]);

  // Mobile: edit and scan as icons so "Lead erfassen" fits (DEV-820).
  const headerActions = event ? (
    <div className="flex flex-wrap items-center gap-2">
      {canManage ? (
        <Button
          type="button"
          variant="outline"
          size={isMobile ? 'icon' : 'md'}
          className={isMobile ? 'size-9' : 'gap-2'}
          aria-label={isMobile ? eventsDict.detail.edit : undefined}
          onClick={() => setEditOpen(true)}
        >
          <Pencil className="h-4 w-4" aria-hidden />
          {isMobile ? null : eventsDict.detail.edit}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size={isMobile ? 'icon' : 'md'}
        className={isMobile ? 'size-9' : 'gap-2'}
        aria-label={isMobile ? eventsDict.detail.scan : undefined}
        onClick={() => setBusinessCardScanOpen(true)}
      >
        <ScanLine className="h-4 w-4" aria-hidden />
        {isMobile ? null : eventsDict.detail.scan}
      </Button>
      <Button
        type="button"
        size="md"
        className="gap-2"
        onClick={() => setCreateLeadHostId((n) => n + 1)}
      >
        <Plus className="h-4 w-4" />
        {eventsDict.detail.addLead}
      </Button>
    </div>
  ) : null;

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader
            title={event?.name ?? eventsDict.title}
            breadcrumb={breadcrumb}
            backHref={shellPaths.leadsEvents}
            actions={headerActions}
          />

          {eventLoading ? (
            <LeadDetailSectionCard>
              <Skeleton className="h-40 w-full" />
            </LeadDetailSectionCard>
          ) : eventError || !event || !workspaceId || !eventId ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 p-5 text-center sm:p-6">
                <p
                  className={`${LEAD_DETAIL_SECTION_TITLE_CLASS} text-muted-foreground`}
                >
                  {getApiErrorStatus(eventError) === 404
                    ? eventsDict.detail.notFound
                    : eventsDict.loadError}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={goBackToEvents}
                >
                  {eventsDict.detail.backToEvents}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-4 md:gap-5">
              {!isOnEventTeam ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] px-4 py-3">
                  <p className="text-[13px] text-foreground">
                    {eventsDict.detail.notOnTeam}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-[color:var(--ghost-accent-fg)]"
                    onClick={() => void handleAddMeToTeam()}
                  >
                    {eventsDict.detail.addMe}
                  </Button>
                </div>
              ) : null}

              <LeadEventDetailSummary
                event={event}
                coverUrl={coverUrl}
                rangeLabel={eventsDict.card.dates
                  .replace('{start}', dateFmt.format(new Date(event.startsAt)))
                  .replace('{end}', dateFmt.format(new Date(event.endsAt)))}
                locationLabel={
                  event.location?.trim() || eventsDict.card.locationUnknown
                }
                teamLabel={teamLabel}
                statusLabel={statusLabel}
                statusMeta={
                  unusualMonths > 0
                    ? eventsDict.detail.unusualDuration.replace(
                        '{count}',
                        String(unusualMonths),
                      )
                    : statusMeta
                }
                autoTagLabel={eventsDict.card.autoTag}
                coverPlaceholder={eventsDict.detail.coverPlaceholder}
                metrics={{
                  leads: analytics?.totalLeads ?? leads.length,
                  leadsLabel: eventsDict.detail.metricsLeads,
                  qualifiedPlus,
                  qualifiedLabel: eventsDict.detail.qualifiedPlus,
                  perDay,
                  perDayLabel: eventsDict.detail.perDay,
                }}
              />

              <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(240px,18rem)] lg:gap-5">
                <div className="min-w-0">
                  {leadsLoading ? (
                    <Skeleton className="h-48 w-full" />
                  ) : (
                    <LeadEventLeadsTable
                      leads={leads}
                      locale={locale}
                      statusLabels={dict.leads.status}
                      sourceLabels={dict.leads.list.source}
                      resolveAssigneeName={(id) =>
                        id
                          ? (memberNameById.get(id) ?? '')
                          : (dict.leads.detail.assigneePicker?.nobody ?? '')
                      }
                      resolveCreatorName={(lead) => {
                        const id =
                          lead.createdByUserId?.trim() || lead.userId?.trim();
                        return id ? (memberNameById.get(id) ?? '') : '';
                      }}
                      title={eventsDict.detail.leadsOfEvent}
                      privateAdminHint={eventsDict.detail.privateAdminHint}
                      emptyTitle={eventsDict.detail.noLeads}
                      emptyDescription={eventsDict.detail.noLeadsHint}
                      columnLabels={{
                        contact: dict.leads.detail.contact,
                        status: dict.leads.detail.status,
                        source: dict.leads.detail.source,
                        assignee: dict.leads.detail.crm.assignee,
                        visibility: dict.leads.detail.visibility,
                        captured: dict.leads.list.columns.captured,
                        workspace: dict.leads.list.cardMeta.visibilityShared,
                        private: dict.leads.list.cardMeta.visibilityPrivate,
                      }}
                      onLeadClick={(lead) =>
                        navigateShell(shellPaths.leadDetail(lead.id))
                      }
                    />
                  )}
                </div>
                <aside className="min-w-0">
                  <LeadEventAnalytics
                    workspaceId={workspaceId}
                    eventId={eventId}
                    layout="sidebar"
                  />
                </aside>
              </div>

              {createLeadHostId > 0 ? (
                <LeadCardList
                  key={createLeadHostId}
                  leads={[]}
                  existingLeads={leads}
                  profileId={profile?.id}
                  workspaceId={workspaceId}
                  hideLeadCards
                  hideAddCards
                  createRequestId={createLeadHostId}
                  onLeadClick={(lead) =>
                    navigateShell(shellPaths.leadDetail(lead.id))
                  }
                  onLeadCreated={() => {
                    invalidateEventLeads();
                    setCreateLeadHostId(0);
                  }}
                  eventIds={[eventId]}
                />
              ) : null}
            </div>
          )}
        </AppPageLayout>

        <BusinessCardScanFlow
          open={businessCardScanOpen}
          onOpenChange={setBusinessCardScanOpen}
          existingLeads={leads}
          profileId={profile?.id}
          workspaceId={workspaceId ?? null}
          onLeadCreated={() => invalidateEventLeads()}
          eventIds={eventId ? [eventId] : undefined}
        />

        <ResponsiveDialog open={editOpen} onOpenChange={setEditOpen}>
          <ResponsiveDialogContent className="flex max-h-[min(90vh,720px)] w-full min-w-0 flex-col overflow-hidden sm:max-w-xl">
            <ResponsiveDialogHeader>
              <ResponsiveDialogTitle>
                {eventsDict.form.editTitle}
              </ResponsiveDialogTitle>
            </ResponsiveDialogHeader>
            {event && workspaceId ? (
              <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-2">
                <LeadEventDetailsForm
                  workspaceId={workspaceId}
                  event={event}
                  canManage={canManage}
                  onSaved={handleSaved}
                  onDelete={() => {
                    setEditOpen(false);
                    setDeleteOpen(true);
                  }}
                />
              </div>
            ) : null}
          </ResponsiveDialogContent>
        </ResponsiveDialog>

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title={(eventsDict.detail.deleteTitle ?? '„{name}“ löschen?').replace(
            '{name}',
            event?.name?.trim() || 'Event',
          )}
          description={
            leads.length > 0
              ? (leads.length === 1
                  ? eventsDict.detail.deleteConfirmWithLeads
                  : (eventsDict.detail.deleteConfirmWithLeadsPlural ??
                    eventsDict.detail.deleteConfirmWithLeads)
                ).replace('{count}', String(leads.length))
              : eventsDict.detail.deleteConfirm
          }
          confirmLabel={eventsDict.detail.delete}
          cancelLabel={eventsDict.form.cancel}
          destructive
          onConfirm={handleDelete}
        />
      </AppPageContent>
    </AppIonPage>
  );
}
